import { HashRouter as Router, Routes, Route } from "react-router-dom";
import { Toasts } from "./components/toast";
import "tw-elements-react/dist/css/tw-elements-react.min.css";
import PrivateRoutes from "./components/protectedRoutes";
import { Suspense, lazy, useCallback, useMemo } from "react";
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/TermsAndConditions"));
import FallbackComponent from "./components/FallbackComponent";
const Loader = lazy(() => import("./components/Loader"));
const Layout = lazy(() => import("./Layout"));
const RegisterCompany = lazy(() => import("./pages/RegisterCompany"));
const Payment = lazy(() => import("./pages/Payment"));
const SetupUserAccount = lazy(() => import("./pages/SetupUserAccount"));
const ViewGeneratedAssessmentPage = lazy(
	() => import("./pages/ViewAssessmentPage")
);
const GenerateAssessmentLaterPage = lazy(
	() => import("./pages/GenerateAssessmentLaterPage")
);
const SendAssessmentPage = lazy(() => import("./pages/SendAssessmentPage"));
const CandidateResume = lazy(() => import("./pages/CandidateResume"));
const Help = lazy(() => import("./pages/Help"));
const ChangePlanPayment = lazy(() => import("./pages/ChangePlanPayment"));
const SelectPlan = lazy(() => import("./pages/SelectPlan"));

// Lazy loaded components
const Home = lazy(() => import("./pages/Home"));
const Application = lazy(() => import("./pages/Application"));
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const BehaviouralTest = lazy(() => import("./pages/BehaviouralTest"));
const ForgetPass = lazy(() => import("./pages/ForgetPass"));
const JobList = lazy(() => import("./pages/JobList"));
const JobDetailsPage = lazy(() => import("./pages/JobDetailsPage"));
const RespJdDash = lazy(() => import("./pages/RespJdDash"));
const InstructionPage = lazy(() => import("./pages/Intruction"));
const AssessmentRequirements = lazy(
	() => import("./pages/AssessmentRequirements")
);
const Success = lazy(() => import("./components/CodingTest/Sucess"));
const RegisterAdmin = lazy(() => import("./pages/RegisterAdmin"));
const AssessmentTermination = lazy(
	() => import("./pages/AssessmentTermination")
);
const TechnicalTest = lazy(() => import("./pages/TechnicalTest"));
const CodingTest = lazy(() => import("./pages/CodingTest"));
const FeedbackForm = lazy(() => import("./pages/FeedbackForm"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const UserLogsInDetail = lazy(() => import("./pages/UserLogsInDetail"));
const AssessmentHighlights = lazy(() => import("./pages/AssessmentHighlights"));
const VerifyToken = lazy(() => import("./pages/VerifyToken"));
const Applicants = lazy(() => import("./pages/Applicants"));
const Settings = lazy(() => import("./pages/Settings"));
import { appsignal } from "@/appSignal";
import { ErrorBoundary } from "@appsignal/react";


function App() {
	const tags = useMemo(() => ({ tag: "value" }), []);
	const fallback = useCallback(() => <FallbackComponent />, []);
	const override = useCallback((span: any) => {
		span.setParams({ customParams: "123" });
		return span
	}, []);
	return (
		<Toasts>
			<ErrorBoundary
				instance={appsignal}
				action="App"
				tags={tags}
				override={override}
				fallback={fallback}
			>
				<main>
					<Router>
						<Suspense
							fallback={
								<div className="flex justify-center items-center h-screen">
									<Loader />
								</div>
							}
						>
							<Routes>
								{/* Public Routes */}

								<Route path="/" element={<Home />} />
								<Route path="/register" element={<Register />} />
								<Route path="/login" element={<Login />} />
								<Route path="/forget-password" element={<ForgetPass />} />
								<Route path="/register-admin" element={<RegisterAdmin />} />
								<Route path="/verify-token/:token" element={<VerifyToken />} />
								<Route
									path="/setup-account/:token"
									element={<SetupUserAccount />}
								/>
								<Route path="/privacy-policy" element={<PrivacyPolicy />} />
								<Route path="/terms-of-service" element={<Terms />} />
								{/* Private Routes */}
								<Route element={<PrivateRoutes />}>
									<Route element={<Layout />}>
										<Route path="/home" element={<AdminDashboard />} />
										<Route
											path="/dashboard/user-logs/:id"
											element={<UserLogsInDetail />}
										/>
										<Route path="/add-job" element={<Application />} />
										<Route path="/jobList" element={<JobList />} />
										<Route path="/job-details/:id" element={<JobDetailsPage />} />
										<Route
											path="/respective-dashboard/:id"
											element={<RespJdDash />}
										/>
										<Route
											path="/respective-dashboard/view-assessment/:id"
											element={<ViewGeneratedAssessmentPage />}
										/>
										<Route
											path="/respective-dashboard/generate-assessment/:id"
											element={<GenerateAssessmentLaterPage />}
										/>
										<Route
											path="/respective-dashboard/send-assessment/:id/:resumeId"
											element={<SendAssessmentPage />}
										/>
										<Route
											path="/respective-dashboard/candidate-resume/:id/:candidateId"
											element={<CandidateResume />}
										/>
										<Route
											path="/applicants/candidate-resume/:candidateId"
											element={<CandidateResume />}
										/>
										<Route path="/applicants" element={<Applicants />} />
										<Route path="/settings" element={<Settings />} />
										<Route path="/help" element={<Help />} />
									</Route>

									<Route path="/select-plan" element={<SelectPlan />} />
									<Route path="/billing-details" element={<RegisterCompany />} />
									<Route path="/payment" element={<Payment />} />
									<Route
										path="/change-plan-payment"
										element={<ChangePlanPayment />}
									/>
								</Route>

								{/* Assessment Routes */}
								<Route
									path="/online-assess/:token"
									element={<AssessmentRequirements />}
								/>
								<Route path="/instruction" element={<InstructionPage />} />
								<Route path="/highlights" element={<AssessmentHighlights />} />
								<Route path="/test" element={<BehaviouralTest />} />
								<Route path="/mcq-main" element={<TechnicalTest />} />
								<Route path="/code" element={<CodingTest />} />
								<Route path="/success" element={<Success />} />
								<Route
									path="/assessment-termination"
									element={<AssessmentTermination />}
								/>
								<Route path="/feedback-form" element={<FeedbackForm />} />
							</Routes>
						</Suspense>
					</Router>
				</main>
			</ErrorBoundary>
		</Toasts>
	);
}

export default App;
